import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { isNativeShell } from '@fc-core/platform/platform.service';

const UPDATE_CHECK_INTERVAL_MS = 5 * 60 * 1000;

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class PwaService {
  private deferredPrompt: BeforeInstallPromptEvent | null = null;
  private installPromptAvailableSubject = new BehaviorSubject<boolean>(false);
  readonly installPromptAvailable$: Observable<boolean> =
    this.installPromptAvailableSubject.asObservable();
  private reloadOnControllerChange = false;
  private controllerChangeListenerAttached = false;
  private updateChecksStarted = false;
  private updateCheckInFlight = false;
  private updatePromptOpen = false;
  private promptedUpdateWorkers = new WeakSet<ServiceWorker>();
  private registrationsWithUpdateListeners =
    new WeakSet<ServiceWorkerRegistration>();

  constructor(private zone: NgZone) {
    if (!this.isBrowserSupported() || this.isStandalone()) {
      return;
    }

    window.addEventListener('beforeinstallprompt', (event: Event) => {
      if (this.deferredPrompt) {
        return;
      }
      event.preventDefault();
      this.deferredPrompt = event as BeforeInstallPromptEvent;
      this.installPromptAvailableSubject.next(true);
    });

    window.addEventListener('appinstalled', () => {
      this.deferredPrompt = null;
      this.installPromptAvailableSubject.next(false);
    });
  }

  canInstall(): boolean {
    return (
      this.isBrowserSupported() && !this.isStandalone() && !!this.deferredPrompt
    );
  }

  async initAppToScreen(): Promise<'accepted' | 'dismissed' | null> {
    if (!this.canInstall()) {
      console.info('Install prompt is not available yet.');
      return null;
    }

    const promptEvent = this.deferredPrompt;
    this.deferredPrompt = null;
    this.installPromptAvailableSubject.next(false);

    try {
      await promptEvent!.prompt();
      const { outcome } = await promptEvent!.userChoice;
      return outcome;
    } catch (error) {
      console.warn('Failed to show native install prompt:', error);
      return null;
    }
  }

  checkForUpdates(): void {
    if (!this.isBrowserSupported() || !('serviceWorker' in navigator)) {
      return;
    }

    this.attachControllerChangeListener();
    this.startUpdateChecks();
  }

  private startUpdateChecks(): void {
    if (this.updateChecksStarted) {
      return;
    }
    this.updateChecksStarted = true;

    this.checkRegistrationForUpdates();

    window.setInterval(() => {
      this.checkRegistrationForUpdates();
    }, UPDATE_CHECK_INTERVAL_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.checkRegistrationForUpdates();
      }
    });
  }

  private notifyAboutUpdate(registration: ServiceWorkerRegistration): void {
    const waitingWorker = registration.waiting;
    if (
      !waitingWorker ||
      this.updatePromptOpen ||
      this.promptedUpdateWorkers.has(waitingWorker)
    ) {
      return;
    }

    this.updatePromptOpen = true;
    const shouldReload = window.confirm(
      'A new version of FleetChaser is available. Reload now to update?',
    );
    this.updatePromptOpen = false;
    this.promptedUpdateWorkers.add(waitingWorker);

    if (!shouldReload) {
      return;
    }

    this.reloadOnControllerChange = true;
    waitingWorker.postMessage({ type: 'SKIP_WAITING' });
  }

  private maybeNotifyAboutUpdate(
    registration: ServiceWorkerRegistration,
  ): void {
    if (registration.waiting && navigator.serviceWorker.controller) {
      this.zone.run(() => this.notifyAboutUpdate(registration));
    }
  }

  private attachUpdateListeners(registration: ServiceWorkerRegistration): void {
    if (this.registrationsWithUpdateListeners.has(registration)) {
      return;
    }
    this.registrationsWithUpdateListeners.add(registration);

    const attachInstallingListener = (
      installing: ServiceWorker,
      reg: ServiceWorkerRegistration,
    ) => {
      installing.addEventListener('statechange', () => {
        if (installing.state === 'installed') {
          this.maybeNotifyAboutUpdate(reg);
        }
      });
    };

    if (registration.installing) {
      attachInstallingListener(registration.installing, registration);
    }

    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing;
      if (!newWorker) {
        return;
      }
      attachInstallingListener(newWorker, registration);
    });
  }

  private async checkRegistrationForUpdates(): Promise<void> {
    if (this.updateCheckInFlight) {
      return;
    }
    this.updateCheckInFlight = true;

    try {
      const registration = await navigator.serviceWorker.ready;

      this.attachUpdateListeners(registration);
      this.maybeNotifyAboutUpdate(registration);

      await registration.update();

      this.maybeNotifyAboutUpdate(registration);
    } catch {
      // Best-effort only; browsers may throttle or ignore forced update checks.
    } finally {
      this.updateCheckInFlight = false;
    }
  }

  private attachControllerChangeListener(): void {
    if (this.controllerChangeListenerAttached) {
      return;
    }
    this.controllerChangeListenerAttached = true;

    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (!this.reloadOnControllerChange) {
        return;
      }
      this.reloadOnControllerChange = false;
      this.zone.run(() => window.location.reload());
    });
  }

  private isBrowserSupported(): boolean {
    return typeof window !== 'undefined' && !isNativeShell();
  }

  private isStandalone(): boolean {
    return (
      window.matchMedia('(display-mode: standalone)').matches ||
      (window.navigator as any).standalone
    );
  }
}
